/**
 * Centralized Logger Utility
 *
 * Replaces console.* statements with a professional, configurable logger.
 *
 * Features:
 * - Dev-only by default (respects NODE_ENV)
 * - Toggleable via window.enableLogger() / window.disableLogger()
 * - Integrates with PostHog in production for error tracking
 * - Performance timing utilities
 * - Structured log levels
 *
 * Usage:
 *   import { logger } from '@/utils/logger';
 *
 *   logger.debug('Debug info', { data });
 *   logger.info('Info message', { data });
 *   logger.warn('Warning', { data });
 *   logger.error('Error occurred', error);
 *
 *   // Performance timing
 *   const timer = logger.time('apiCall');
 *   await fetchData();
 *   timer.end();
 *
 * Console commands (production):
 *   window.enableLogger()  - Enable logging in production
 *   window.disableLogger() - Disable logging
 */

import { trackEvent, isPostHogEnabled } from '../config/posthog.config';

// Log levels
export enum LogLevel {
  DEBUG = 0,

  INFO = 1,

  WARN = 2,

  ERROR = 3,

  NONE = 4
}

// Logger configuration
interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  analyticsEnabled: boolean;
}

class Logger {
  private config: LoggerConfig = {
    enabled: import.meta.env.DEV, // Dev-only by default
    level: LogLevel.DEBUG,
    analyticsEnabled: import.meta.env.PROD // PostHog only in prod
  };

  private timers: Map<string, number> = new Map();

  constructor() {
    // Expose global controls for production debugging
    if (typeof window !== 'undefined') {
      (window as any).enableLogger = () => this.enable();
      (window as any).disableLogger = () => this.disable();
      (window as any).setLogLevel = (level: keyof typeof LogLevel) =>
        this.setLevel(LogLevel[level]);
    }
  }

  /**
   * Enable logging (useful in production for debugging)
   */
  enable(): void {
    this.config.enabled = true;
    this.info('[Logger] Enabled');
  }

  /**
   * Disable logging
   */
  disable(): void {
    this.info('[Logger] Disabled');
    this.config.enabled = false;
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
    this.info(`[Logger] Level set to ${LogLevel[level]}`);
  }

  /**
   * Debug logging (verbose, dev-only typically)
   */
  debug(message: string, ...args: any[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    console.debug(`[DEBUG] ${message}`, ...args);
  }

  /**
   * Info logging (general information)
   */
  info(message: string, ...args: any[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    console.info(`ℹ️  ${message}`, ...args);
  }

  /**
   * Warning logging
   */
  warn(message: string, ...args: any[]): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    console.warn(`⚠️  ${message}`, ...args);

    // Send to PostHog in production
    if (this.config.analyticsEnabled && isPostHogEnabled()) {
      trackEvent('logger_warning', {
        message,
        level: 'warn',
        ...this.flattenArgs(args)
      });
    }
  }

  /**
   * Error logging
   */
  error(message: string, error?: Error | unknown, ...args: any[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    console.error(`❌ ${message}`, error, ...args);

    // Send to PostHog in production
    if (this.config.analyticsEnabled && isPostHogEnabled()) {
      const errorMessage = error instanceof Error ? error.message : String(error || message);
      const errorStack = error instanceof Error ? error.stack : undefined;

      trackEvent('logger_error', {
        message,
        error_message: errorMessage,
        error_stack: errorStack,
        level: 'error',
        ...this.flattenArgs(args)
      });
    }
  }

  /**
   * Group logging (collapsible in console)
   */
  group(label: string, callback: () => void): void {
    if (!this.config.enabled) return;

    console.group(label);
    callback();
    console.groupEnd();
  }

  /**
   * Collapsed group logging
   */
  groupCollapsed(label: string, callback: () => void): void {
    if (!this.config.enabled) return;

    console.groupCollapsed(label);
    callback();
    console.groupEnd();
  }

  /**
   * Table logging (for arrays/objects)
   */
  table(data: any): void {
    if (!this.config.enabled) return;

    console.table(data);
  }

  /**
   * Start a performance timer
   */
  time(label: string): { end: () => void } {
    if (!this.config.enabled) {
      return { end: () => {} };
    }

    const startTime = performance.now();
    this.timers.set(label, startTime);

    return {
      end: () => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        this.timers.delete(label);

        const formatted =
          duration < 1000 ? `${duration.toFixed(2)}ms` : `${(duration / 1000).toFixed(2)}s`;

        this.info(`⏱️  ${label}: ${formatted}`);

        // Send to PostHog as performance metric
        if (this.config.analyticsEnabled && isPostHogEnabled()) {
          trackEvent('performance_timing', {
            operation: label,
            duration_ms: duration
          });
        }
      }
    };
  }

  /**
   * Log a network request
   */
  network(method: string, url: string, status?: number, duration?: number): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const statusIcon = status
      ? status >= 200 && status < 300
        ? '✅'
        : status >= 400
          ? '❌'
          : '⚠️'
      : '🔄';

    const durationStr = duration ? ` (${duration}ms)` : '';
    const statusStr = status ? ` ${status}` : '';

    this.info(`${statusIcon} ${method} ${url}${statusStr}${durationStr}`);
  }

  /**
   * Check if we should log at this level
   */
  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level >= this.config.level;
  }

  /**
   * Flatten arguments for PostHog event properties
   */
  private flattenArgs(args: any[]): Record<string, any> {
    if (args.length === 0) return {};

    return args.reduce((acc, arg, index) => {
      if (typeof arg === 'object' && arg !== null) {
        return { ...acc, ...arg };
      }
      acc[`arg${index}`] = String(arg);
      return acc;
    }, {});
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
